import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { defer, filter, map, Observable, of, take } from 'rxjs';

/**
 * 🌐 Shape of the global `window.o_idzone` object exposed by IDZone/Elcos.
 * Only the keys consumed by the app are typed explicitly; the index signature
 * keeps forward-compatibility for any other key still read via bracket access.
 */
export interface IdZoneData {
    USER_IDENT_LEVEL?: string;
    USER_EXTERNAL_ID_HASH?: string;
    CLIENT_ID?: string;
    [key: string]: string | undefined;
}

declare global {
    interface Window {
        o_idzone?: IdZoneData;
        o_idzone_is_loaded?: boolean;
    }
    interface WindowEventMap {
        'idzone:loaded': CustomEvent;
    }
}

/**
 * 🎯 Single source of truth for the asynchronously loaded `window.o_idzone` data.
 *
 * IDZone/Elcos loads `window.o_idzone` asynchronously (from April 2026):
 * - an `idzone:loaded` event on `window` signals the data availability;
 * - `window.o_idzone_is_loaded` tells whether the event was already emitted
 *   (the "already loaded" case);
 * - a 3000ms timeout emits a first `idzone:loaded` with default (visitor) data,
 *   then a second `idzone:loaded` may follow with the real data.
 *
 * This service abstracts away `window.o_idzone`: data are considered unreliable
 * until the event has been emitted, even though the global may already exist for
 * backward-compatibility. It is SSR-aware and stays inert on the server. 😎
 */
@Injectable({ providedIn: 'root' })
export class IdZoneService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly destroyRef = inject(DestroyRef);

    private readonly _data = signal<IdZoneData | undefined>(undefined);
    private readonly _loaded = signal(false);

    /** Current data snapshot captured from the async event stream. */
    readonly data = this._data.asReadonly();
    /** Whether an `idzone:loaded` event has been received at least once. */
    readonly loaded = this._loaded.asReadonly();

    /** Reactive flow (all updates, including the 2nd event) — for reactive consumers. */
    readonly data$: Observable<IdZoneData | undefined> = toObservable(this._data);

    private readonly loaded$: Observable<boolean> = toObservable(this._loaded);

    /**
     * Emits once, as soon as the data are available:
     * - immediately if the data are already there (legacy synchronous access, or
     *   async "already loaded");
     * - otherwise on the 1st `idzone:loaded` event.
     *
     * Evaluated at subscription time (via `defer`), NOT at construction, to avoid
     * reading `o_idzone` too early (before the external script has set it).
     */
    readonly whenLoaded$: Observable<IdZoneData | undefined> = defer(() => {
        if (!isPlatformBrowser(this.platformId)) {
            return of(undefined);
        }
        if (globalThis.o_idzone_is_loaded || globalThis.o_idzone) {
            return of(this.currentData());
        }
        return this.loaded$.pipe(
            filter((loaded) => loaded === true),
            take(1),
            map(() => this.currentData()),
        );
    });

    /** Typed accessor — USER_IDENT_LEVEL. */
    public userIdentLevel(): string | undefined {
        return this.currentData()?.['USER_IDENT_LEVEL'];
    }

    /** Typed accessor — USER_EXTERNAL_ID_HASH (ise2). */
    public userExternalIdHash(): string | undefined {
        return this.currentData()?.['USER_EXTERNAL_ID_HASH'];
    }

    /** Typed accessor — CLIENT_ID. */
    public clientId(): string | undefined {
        return this.currentData()?.['CLIENT_ID'];
    }

    public constructor() {
        // SSR: stay inert (loaded=false, data=undefined, no listener attached).
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }
        if (globalThis.o_idzone_is_loaded) {
            // Async "already loaded": the 1st event won't fire again, snapshot now.
            this.capture();
        }
        // Always listen: captures the 1st async event (once enabled) and any 2nd
        // event (real data after the default/timeout payload). No { once: true }.
        const handler = (): void => this.capture();
        globalThis.addEventListener('idzone:loaded', handler);
        this.destroyRef.onDestroy(() => globalThis.removeEventListener('idzone:loaded', handler));
    }

    /**
     * Resolves the current IDZone data through the service (single source of truth).
     * Prefers the event-captured snapshot; during the transition it falls back to
     * reading `globalThis.o_idzone` live for backward-compatibility.
     *
     * 🔧 TRANSITION: once the async `idzone:loaded` event is enabled everywhere,
     * drop the `?? this.readLegacyGlobal()` fallback to enforce the strict
     * "trust only after event" semantics.
     */
    private currentData(): IdZoneData | undefined {
        return this._data() ?? this.readLegacyGlobal();
    }

    /** Legacy synchronous read of the global — isolated so it's trivial to remove. */
    private readLegacyGlobal(): IdZoneData | undefined {
        return isPlatformBrowser(this.platformId) ? globalThis.o_idzone : undefined;
    }

    private capture(): void {
        this._data.set(globalThis.o_idzone);
        this._loaded.set(true);
    }
}
